/**
 * Navigation Configuration
 * PRD-aligned navigation items per user type
 */

import {
	Briefcase,
	ClipboardList,
	FileText,
	FolderKanban,
	Hammer,
	Home,
	ImageIcon,
	Inbox,
	Layers,
	type LucideIcon,
	MessageSquare,
	Search,
	Settings,
	Sparkles,
	UserRound,
	Users,
} from "lucide-react";

export interface NavItem {
	label: string;
	/** i18n key; when set, the sidebar shows its translation instead of `label`. */
	labelKey?: string;
	/** A shorter name for the phone bottom bar, which has less room per tab. */
	shortLabel?: string;
	/** i18n key for `shortLabel`. */
	shortLabelKey?: string;
	path: string;
	icon: LucideIcon;
}

// ---------------------------------------------------------------------------
// Sidebar main nav (desktop) -- per user type
// ---------------------------------------------------------------------------

const HOMEOWNER_NAV: NavItem[] = [
	{ label: "Home", path: "/home", icon: Home },
	{ label: "Inspire", path: "/inspire", icon: Sparkles },
	{ label: "Designers", path: "/designers", icon: Search },
	{ label: "Contractors", path: "/contractors", icon: Hammer },
	{ label: "Projects", path: "/projects", icon: FolderKanban },
	{ label: "Catalog", path: "/catalog", icon: Layers },
];

const DESIGNER_NAV: NavItem[] = [
	{ label: "Home", path: "/dashboard", icon: Home },
	{ label: "Requests", path: "/requests", icon: FileText },
	{ label: "Projects", path: "/projects", icon: FolderKanban },
	{ label: "Portfolio", path: "/portfolio", icon: ImageIcon },
	{ label: "Catalog", path: "/catalog", icon: Layers },
];

const CONTRACTOR_NAV: NavItem[] = [
	{ label: "Home", path: "/dashboard", icon: Home },
	{ label: "Opportunities", path: "/opportunities", icon: Briefcase },
	{ label: "My Work", path: "/assignments", icon: ClipboardList },
	{ label: "Profile", path: "/contractor-profile", icon: ImageIcon },
	{ label: "Catalog", path: "/catalog", icon: Layers },
];

const REALTOR_NAV: NavItem[] = [
	{
		label: "Home",
		labelKey: "realtor.nav.home",
		path: "/dashboard",
		icon: Home,
	},
	{
		label: "Pipeline (Leads)",
		labelKey: "realtor.nav.leads",
		shortLabel: "Pipeline",
		shortLabelKey: "realtor.nav.leadsShort",
		path: "/realtor/leads",
		icon: UserRound,
	},
	{
		label: "Listings",
		labelKey: "realtor.nav.listings",
		path: "/realtor/listings",
		icon: Layers,
	},
	{
		label: "Enquiries",
		labelKey: "realtor.nav.enquiries",
		path: "/realtor/enquiries",
		icon: Inbox,
	},
	{
		label: "Contacts",
		labelKey: "realtor.nav.contacts",
		path: "/realtor/contacts",
		icon: Users,
	},
	{
		label: "Messages",
		labelKey: "realtor.nav.messages",
		path: "/realtor/messages",
		icon: MessageSquare,
	},
	{
		label: "Portfolio",
		labelKey: "realtor.nav.portfolio",
		path: "/realtor/portfolio",
		icon: ImageIcon,
	},
];

/**
 * Whether the nav entry for `path` is the current page. The two home routes
 * match only themselves; every other entry also owns the routes beneath it.
 */
export function isNavPathActive(pathname: string, path: string): boolean {
	if (path === "/dashboard" || path === "/home") {
		return pathname === path;
	}
	return pathname.startsWith(path);
}

export function getMainNav(userType?: string): NavItem[] {
	switch (userType) {
		case "designer":
			return DESIGNER_NAV;
		case "contractor":
			return CONTRACTOR_NAV;
		case "realtor":
			return REALTOR_NAV;
		default:
			return HOMEOWNER_NAV;
	}
}

// ---------------------------------------------------------------------------
// Bottom tab bar (below the mobile breakpoint) -- per user type
// ---------------------------------------------------------------------------

// Paths picked out of the role's main nav above, so a tab carries the same
// label, i18n key and icon as that role's entry in `getMainNav`.
const BOTTOM_NAV_PATHS: Record<string, readonly string[]> = {
	homeowner: ["/home", "/designers", "/projects"],
	designer: ["/dashboard", "/requests", "/projects"],
	contractor: ["/dashboard", "/opportunities", "/assignments"],
	realtor: ["/dashboard", "/realtor/leads", "/realtor/listings"],
};

export function getBottomNav(userType?: string): NavItem[] {
	const paths =
		BOTTOM_NAV_PATHS[userType ?? "homeowner"] ?? BOTTOM_NAV_PATHS.homeowner;
	return getMainNav(userType)
		.filter((item) => paths.includes(item.path))
		.map((item) => ({
			...item,
			label: item.shortLabel ?? item.label,
			labelKey: item.shortLabelKey ?? item.labelKey,
		}));
}

// ---------------------------------------------------------------------------
// Secondary nav (all user types)
// ---------------------------------------------------------------------------

export const SECONDARY_NAV: NavItem[] = [
	{ label: "Settings", path: "/settings", icon: Settings },
];
